import React, { useState } from 'react';
import { History, Loader2, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { api, errMsg } from './api';
import { useDeck } from './store';
import { ago } from './Editable';

type Version = { v: any; by: string; at: string };

export function HistoryButton({ k, testId }: { k: string; testId: string }) {
  const { slug, get, set } = useDeck();
  const [items, setItems] = useState<Version[] | null>(null);
  const [open, setOpen] = useState(false);
  const current: string = get(k) || '';

  const load = async (o: boolean) => {
    setOpen(o);
    if (!o) return;
    setItems(null);
    try { setItems(await api.history(slug, k)); }
    catch (e) { toast.error(errMsg(e)); setOpen(false); }
  };

  const restore = (v: string) => { set(k, v); toast.success('Version restaurée.'); setOpen(false); };

  return (
    <Popover open={open} onOpenChange={load}>
      <PopoverTrigger asChild>
        <button
          data-testid={`${testId}-history-btn`}
          title="Annuler une modification (historique)"
          className={`ed-meta absolute right-1 top-1 z-30 flex h-6 w-6 items-center justify-center rounded-full bg-neutral-900 text-white shadow hover:bg-blue-600 transition-colors ${open ? '!opacity-100' : ''}`}
        >
          <History size={13} />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[380px] p-2 font-ui" align="end" data-testid={`${testId}-history`}>
        <p className="px-2 pb-2 text-xs font-semibold uppercase tracking-wider text-neutral-500">Versions précédentes</p>
        {!items && <div className="flex justify-center py-4"><Loader2 className="animate-spin" size={18} /></div>}
        {items && items.length === 0 && <p className="px-2 py-3 text-sm text-neutral-500">Aucune version enregistrée pour cette zone.</p>}
        <div className="max-h-[320px] overflow-auto">
          {items?.map((it, i) => {
            const text = typeof it.v === 'string' ? it.v : '';
            const isCurrent = i === 0 && text === current;
            return (
              <div key={it.at} className="flex items-start gap-2 rounded-lg px-2 py-2 hover:bg-neutral-50" data-testid={`${testId}-history-item-${i}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-xs text-neutral-500"><b className="text-neutral-800">{it.by}</b> · {ago(it.at)}</div>
                  <div className="mt-0.5 line-clamp-2 text-sm text-neutral-700">{text.trim() || <i className="text-neutral-400">(zone vide)</i>}</div>
                </div>
                {isCurrent ? (
                  <span className="shrink-0 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">Actuelle</span>
                ) : (
                  <button data-testid={`${testId}-history-restore-${i}`} onClick={() => restore(text)} className="flex shrink-0 items-center gap-1 rounded-full bg-black px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-blue-600 transition-colors">
                    <RotateCcw size={11} /> Restaurer
                  </button>
                )}
              </div>
            );
          })}
        </div>
        {current && (
          <button data-testid={`${testId}-history-clear`} onClick={() => restore('')} className="mt-1 w-full rounded-lg px-2 py-1.5 text-left text-xs text-neutral-500 hover:bg-neutral-50 hover:text-red-600">Vider la zone</button>
        )}
      </PopoverContent>
    </Popover>
  );
}
